import api from './api';

class ProductService {
  // Get all products with pagination
  async getProducts(page = 1, limit = 12) {
    try {
      const response = await api.get(`/store/products?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  // Get products by category
  async getProductsByCategory(categoryId, page = 1, limit = 12) {
    try {
      const response = await api.get(`/store/products?category_id=${categoryId}&page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching products by category:', error);
      throw error;
    }
  }

  // Get products by category ID using new endpoint
  async getProductsByCategoryId(categoryId) {
    try {
      const response = await api.get(`/store/products/category/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching products by category ID:', error);
      throw error;
    }
  }

  // Get single product by ID
  async getProductById(id) {
    try {
      const response = await api.get(`/store/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  // Get single product by slug
  async getProductBySlug(slug) {
    try {
      const response = await api.get(`/store/products/${slug}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product by slug:', error);
      throw error;
    }
  }

  // Get featured products
  async getFeaturedProducts(limit = 8) {
    try {
      const response = await api.get(`/store/products?is_featured=true&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  }

  // Search products
  async searchProducts(query, page = 1, limit = 12) {
    try {
      const response = await api.get(`/store/products?search=${query}&page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  // Helper function to normalize image array from different API formats
  normalizeImageArray(apiProduct) {
    // Eğer zaten image array varsa ve geçerliyse, onu kullan
    if (Array.isArray(apiProduct.image) && apiProduct.image.length > 0) {
      return apiProduct.image;
    }
    
    // primary_image ve secondary_image varsa
    const images = [];
    if (apiProduct.primary_image) {
      images.push(apiProduct.primary_image);
    }
    if (apiProduct.secondary_image) {
      images.push(apiProduct.secondary_image);
    }
    
    // product_images array'i varsa
    if (images.length === 0 && apiProduct.product_images && Array.isArray(apiProduct.product_images)) {
      const sortedImages = apiProduct.product_images
        .sort((a, b) => {
          if (a.is_primary && !b.is_primary) return -1;
          if (!a.is_primary && b.is_primary) return 1;
          return (a.sort_order || 0) - (b.sort_order || 0);
        })
        .map(img => img.url || img);
      images.push(...sortedImages);
    }
    
    // Fallback: main_image varsa
    if (images.length === 0 && apiProduct.main_image) {
      images.push(apiProduct.main_image);
    }
    
    // Eğer hiç image yoksa, varsayılan placeholder
    if (images.length === 0) {
      images.push('/assets/img/product/fashion/1.jpg');
    }
    
    return images;
  }

  // Transform API data - only transform variation structure, keep rest as is
  transformProductData(apiProduct) {
    // API'den gelen veriyi olduğu gibi kullan, sadece variation'ı dönüştür
    const transformedProduct = { ...apiProduct };
    
    // sku alanını slug'dan oluştur (eğer sku yoksa)
    if (!transformedProduct.sku && transformedProduct.slug) {
      transformedProduct.sku = transformedProduct.slug;
    }
    // Eğer ne sku ne slug varsa, id'yi kullan
    if (!transformedProduct.sku && !transformedProduct.slug && transformedProduct.id) {
      transformedProduct.sku = transformedProduct.id.toString();
    }
    
    // Image array'ini normalize et
    transformedProduct.image = this.normalizeImageArray(apiProduct);
    
    // API'den gelen chain_colors, chain_types, available_sizes'ı direkt kullan
    // Artık variation kombinasyonu oluşturmuyoruz, her biri ayrı seçim olacak
    // Bu alanlar zaten API'den geliyor, olduğu gibi kullanıyoruz
    
    return transformedProduct;
  }

  // Get transformed products for the app - only transforms variation
  async getTransformedProducts(page = 1, limit = 12) {
    try {
      const response = await this.getProducts(page, limit);
      if (response.success && response.data) {
        // Sadece variation'ı dönüştür, diğer verileri olduğu gibi kullan
        const transformedProducts = response.data.map(product => this.transformProductData(product));
        return {
          success: true,
          data: transformedProducts,
          pagination: response.pagination
        };
      }
      return { success: false, data: [], pagination: null };
    } catch (error) {
      console.error('Error getting transformed products:', error);
      return { success: false, data: [], pagination: null };
    }
  }

  // Get transformed products by category ID
  async getTransformedProductsByCategory(categoryId) {
    try {
      const response = await this.getProductsByCategoryId(categoryId);
      if (response.success && response.data) {
        // Sadece variation'ı dönüştür, diğer verileri olduğu gibi kullan
        // Status inactive olanları da dahil et (filtreleme yapmıyoruz)
        const transformedProducts = response.data.map(product => this.transformProductData(product));
        return {
          success: true,
          data: transformedProducts,
          pagination: response.pagination
        };
      }
      return { success: false, data: [], pagination: null };
    } catch (error) {
      console.error('Error getting transformed products by category:', error);
      return { success: false, data: [], pagination: null };
    }
  }
}

export default new ProductService();
